import axios from "axios";
import { notification } from "antd";
import i18n from "../i18n";
import { apiErrorText } from "../utils/apiError";
import { URL } from "../utils/contact";

const responseBody = (response) => response.data;

/**
 * 403 안내를 띄웁니다.
 *
 * 라이선스 유예가 끝나 제어 API 가 차단된 경우와 단순 권한 부족을 구분합니다.
 * 백엔드 LicenseEnforcementInterceptor 는 error 필드에 'License required' 를 담습니다.
 */
const showForbidden = (data) => {
  const isLicenseBlock = data?.error === 'License required';
  // data.error 로 폴백하지 않습니다. Spring Security 가 컨트롤러 앞에서 거부하면
  // 본문이 {"error":"Forbidden","message":""} 이라 한국어 화면에 영문이 노출됩니다.
  const detail = apiErrorText(data, 'err.forbiddenDesc');
  notification.warning({
    // 연속 차단 시 알림이 쌓이지 않도록 같은 키를 재사용합니다.
    key: isLicenseBlock ? 'license-readonly' : 'forbidden',
    message: i18n.t(isLicenseBlock ? 'err.readOnlyTitle' : 'err.forbiddenTitle'),
    description: detail,
    // 앱 전체 알림이 'top' 을 쓰므로 위치를 맞춥니다.
    placement: 'top',
    duration: 6,
  });
};

/**
 * raw fetch 응답이 403 이면 안내를 띄우고 표시가 붙은 Error 를 돌려줍니다.
 * 403 이 아니면 null 입니다.
 *
 * 업로드 경로(파일/SAR)는 진행률 처리 때문에 axios 가 아니라 fetch 를 직접 쓰므로
 * 아래 응답 인터셉터를 타지 않습니다. 그래서 차단 안내가 누락되고 있었습니다.
 * 문구가 두 벌로 갈리지 않도록 인터셉터와 같은 showForbidden 을 공유합니다.
 */
export const forbiddenFromResponse = async (response) => {
  if (response.status !== 403) return null;
  let data = null;
  try {
    // clone 해서 읽습니다. 호출부가 본문을 다시 읽을 수 있어야 합니다.
    data = await response.clone().json();
  } catch (e) {
    // 프록시가 HTML 을 돌려주는 등 JSON 이 아닐 수 있습니다.
  }
  showForbidden(data);
  const error = new Error('FORBIDDEN');
  error.forbidden = true;
  return error;
};

/**
 * 이미 안내가 끝난 403 인지 판단합니다.
 * 호출부가 같은 내용을 한 번 더 띄우지 않도록 쓰는 용도입니다.
 */
export const isForbidden = (error) =>
  Boolean(error?.forbidden) || error?.response?.status === 403 || error?.status === 403;

const mergeUrl = (url) => {
  return `${URL}${url}`;
};

// Nginx 프록시를 사용하도록 baseURL 설정
// URL이 설정되어 있으면 직접 호출 (로컬 개발)
// URL이 비어있으면 상대 경로 사용 (Nginx 프록시 경유)
const apiClient = axios.create({
  baseURL: URL || '', // URL이 없으면 상대 경로로 Nginx 프록시 사용
});

apiClient.interceptors.request.use((config) => {
  // 저장된 JWT 를 모든 요청에 실어 보냅니다.
  // permitAll 경로는 이 값을 사용하지 않지만, 인증이 필요한 /api/license/** 는
  // 이 헤더가 없으면 401 로 떨어지고 아래 응답 인터셉터가 로그인 화면으로
  // 되돌려 보내 화면 자체가 뜨지 않습니다.
  try {
    const stored = localStorage.getItem('user');
    if (stored) {
      const token = JSON.parse(stored)?.accessToken;
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
  } catch (e) {
    // 저장값이 손상되었더라도 요청 자체는 진행합니다.
    console.log('저장된 인증 정보를 읽지 못했습니다.');
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      const { status, data } = error.response;
      const currentPath = window.location.pathname;
      const requestUrl = error.config?.url || '';
      
      // 에러 페이지에서는 리다이렉트하지 않음
      if (currentPath.includes('/error')) {
        return Promise.reject(error);
      }
      
      // 오류 메시지 추출 (백엔드 응답 형식에 따라 조정)
      const errorMessage = data?.message || data?.error || '알 수 없는 오류가 발생했습니다';
      
      if (status === 401) {
        // 로그인 페이지이거나 로그인 API 호출 시에는 리다이렉트하지 않음
        if (currentPath === '/login' || requestUrl.includes('/auth/authenticate')) {
          console.log(`401 오류 (로그인 페이지): ${errorMessage}`);
          return Promise.reject(error);
        }
        window.location.href = "/login";
      } else if (status === 404) {
        // 로그인 페이지에서는 리다이렉트하지 않음
        if (currentPath === '/login' || requestUrl.includes('/auth/authenticate')) {
          console.log(`404 오류 (로그인 페이지): ${errorMessage}`);
          return Promise.reject(error);
        }
        console.log(`404 오류: ${errorMessage}`);
        window.location.href = '/error404';
      } else if (status === 400) {
        // 400 에러: Bad Request (데이터베이스 연결 오류 등 포함)
        if (currentPath === '/login' || requestUrl.includes('/auth/authenticate')) {
          console.log(`400 오류 (로그인 페이지): ${errorMessage}`);
          return Promise.reject(error);
        }
        console.log(`400 오류: ${errorMessage}`);
        // DB 연결 오류 등 서버 문제는 500 페이지로
        if (errorMessage.includes('Connection') || errorMessage.includes('JDBC') || errorMessage.includes('Database')) {
          window.location.href = '/error500';
        } else {
          // 일반 요청 오류는 rejection만
          return Promise.reject(error);
        }
      } else if (status === 403) {
        // 403 은 리다이렉트하지 않고 현재 화면에 알립니다. 사용자가 하던 작업을
        // 잃지 않게 하기 위해서입니다.
        //
        // 알림을 여기서 띄우는 이유: 각 페이지의 호출부는 store/api/action.js 의
        // getData/putData/deleteData 를 거치는데, 이들이 실패를 { data: undefined }
        // 로 삼켜버립니다. 페이지마다 개별 처리를 붙이지 않는 한 사용자는 아무
        // 표시 없이 실패를 겪게 됩니다.
        showForbidden(data);
        console.log(`403 오류: ${errorMessage}`);
        return Promise.reject(error);
      } else if (status === 500 || status === 503) {
        // 500: Internal Server Error, 503: Service Unavailable
        if (currentPath === '/login' || requestUrl.includes('/auth/authenticate')) {
          console.log(`${status} 오류 (로그인 페이지): ${errorMessage}`);
          return Promise.reject(error);
        }
        console.log(`${status} 오류: ${errorMessage}`);
        window.location.href = '/error500';
      } else {
        // 다른 오류 상태 코드 처리
        console.log(`${status} 오류: ${errorMessage}`);
        return Promise.reject(error);
      }
    } else if (error.request) {
      // 요청은 전송되었으나 응답을 받지 못함 (네트워크 오류)
      const currentPath = window.location.pathname;
      const requestUrl = error.config?.url || '';
      
      // 에러 페이지나 로그인 페이지에서는 리다이렉트하지 않음
      if (currentPath.includes('/error') || currentPath === '/login' || requestUrl.includes('/auth/authenticate')) {
        console.log("네트워크 오류 또는 서버 응답 없음");
        return Promise.reject(error);
      }
      
      console.log("네트워크 오류 또는 서버 응답 없음");
      window.location.href = '/error500';
    } else {
      // 요청 설정 중 문제 발생
      console.log("요청 설정 오류:", error.message);
    }
    return Promise.reject(error);
  }
);

/**
 * Build common method for support get, post, put, delete
 */
const request = {
  get: (url) => apiClient.get(url).then(responseBody),
  getRequestWithHeader: (url, config) => apiClient.get(url, config).then(responseBody),
  post: (url, body) => apiClient.post(url, body).then(responseBody),
  postRequestWithHeader: (url, body, config) =>
    apiClient.post(url, body, config).then(responseBody),
  put: (url, body) => apiClient.put(url, body).then(responseBody),
  patchRequestWithHeader: (url, body, config) =>
    apiClient.patch(url, body, config).then(responseBody),
  delete: (url, config) => apiClient.delete(url, config).then(responseBody),
};

export { request, mergeUrl };
